import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Sparkles, Send, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";

export default function RatedThanks() {
  const [params] = useSearchParams();
  const ratingId = params.get("id");
  const stars = Number(params.get("stars") || 0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!comment.trim()) return;
    setSubmitting(true); setError("");
    try {
      await api.post(`/ratings/${ratingId}/comment`, {
        comment: comment.trim(),
        customer_name: name.trim() || undefined,
      });
      setSubmitted(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-white">
      <Navbar />
      <section className="pt-36 pb-20 relative overflow-hidden">
        <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] bg-cyan-500/15 blur-[140px] rounded-full" />
        <div className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] bg-violet-500/15 blur-[140px] rounded-full" />
        <div className="relative mx-auto max-w-2xl px-6 lg:px-10 text-center">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-tr from-cyan-400 to-teal-400 grid place-items-center glow-cyan">
              <CheckCircle2 className="w-8 h-8 text-black" />
            </div>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">/ rating recorded</p>
            <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tight mt-3">
              Thank you.
            </h1>
            <div className="mt-6 flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-7 h-7 ${n <= stars ? "fill-yellow-400 text-yellow-400" : "text-slate-700"}`}
                />
              ))}
            </div>
            {!ratingId ? (
              <p className="text-[#5A5A72] mt-6">No rating ID found in this link.</p>
            ) : submitted ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
                <div className="glass-card rounded-2xl p-8" data-testid="rated-thanks-final">
                  <Sparkles className="w-6 h-6 mx-auto text-[#00D4FF] mb-3" />
                  <p className="font-display font-bold text-xl">You're amazing.</p>
                  <p className="text-[#5A5A72] mt-2">If approved by Vedant, your kind words will appear on the Xion landing page.</p>
                  <Link to="/" className="mt-6 inline-flex btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">Back to Xion</Link>
                </div>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10">
                <p className="text-[#4B5563] mb-6">
                  {stars >= 4
                    ? "Your rating is in. Want to leave a few words for the world to see?"
                    : "Your rating is in. Tell us what we could do better — we read every word."}
                </p>
                <div className="glass-card rounded-2xl p-7 text-left">
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Your name (optional)</label>
                  <input
                    value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="rated-name-input"
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none"
                    placeholder="How should we credit you?"
                  />
                  <label className="block mt-5 text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Your message</label>
                  <textarea
                    rows={5} value={comment} onChange={(e) => setComment(e.target.value)}
                    data-testid="rated-comment-input"
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none resize-none"
                    placeholder={stars >= 4 ? "Xion turned our pitch deck into..." : "Be honest — what would have made this better?"}
                  />
                  {error && <p className="text-red-400 text-sm mt-3" data-testid="rated-error">{error}</p>}
                  <button
                    onClick={submit} disabled={submitting || !comment.trim()}
                    data-testid="rated-submit-btn"
                    className="mt-5 w-full btn-primary py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : <>Send to Vedant <Send className="w-4 h-4" /></>}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
